import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { getLocalAppSession, setLocalAppSession } from 'utils/storageHelpers';
import { useQuery } from 'hooks/sympl-query';
import { GET_VACANCY } from 'graphql/vacancies/queries';
import { ApolloQueryResult } from '@apollo/client';
import { GET_SESSION_FLAGS } from 'graphql/auth/queries';
import { Routes } from 'types/routeTypes';
import { Vacancy, VacancySpendStatus } from 'types/vacancyTypes';
import { Brand } from 'types/customer/types';
import { GET_CUSTOMER } from 'graphql/customers/queries';
import { useRefetchOnCustomerChange } from 'hooks/useRefetchOnCustomerChange';
import { Customer } from 'types/customer/types';
import {
  AD_TEMPLATE_LEVELS,
  AdTemplateLevel,
  Subscription,
  SUBSCRIPTION_CAPABILITIES,
  SubscriptionCapabilityKey,
  SubscriptionConfiguration,
} from 'types/subscriptions/types';
import { GET_BRANDS } from 'graphql/brands/queries';
import { useNavigate } from 'react-router-dom';

import posthog from 'posthog-js';
import { updateIntercom } from 'utils/intercomHelper';
import { GET_SUBSCRIPTION_CONFIGURATION } from 'graphql/subscriptions/queries';

type NavigationContextType = {
  activeVacancy?: number;
  activeBrand?: number;
  currentVacancy?: Vacancy;
  resetContext: () => void;
  setActiveVacancy: (config: ActiveVacancySetterConfig) => void;
  unsetActiveVacancy: () => void;
  activeCustomer?: number;
  setActiveCustomer: (customerId?: number) => void;
  vacIsPublished: boolean;
  vacSpendEnabled: boolean;
  isAdmin: boolean;
  jobPosition: string;
  refetchCurrentVacancy: () => void;
  hasVacancies: boolean;
  hasPublishedVacancy: boolean;
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: Dispatch<SetStateAction<boolean>>;
  prevLocation: string;
  setPrevLocation: (location: string) => void;
  clearResourceSelection: () => void;
  setClearResourceSelection: Dispatch<SetStateAction<() => void>>;
  customerColors: string[];
  customer: Customer | null; // TODO: can we drop null?
  subscription: Subscription | null;
  subscriptionConfiguration: SubscriptionConfiguration | null;
  hasSubscriptionCapability: (capability: SubscriptionCapabilityKey) => boolean;
  hasAdTemplates: boolean;
  adTemplateLevel: AdTemplateLevel;
  hasReachedVacsLimit: boolean;
  refetchCustomer: () => Promise<ApolloQueryResult<{ customer: Customer }>>;
  hasSymplVacancyPage: boolean;
  brands: Brand[];
  refetchBrands: () => void;
  vacancyLoading: boolean;
};

interface SessionFlagsResponse {
  flags?: {
    user: {
      job_position?: string;
      is_sympl?: boolean;
    };
  };
}

interface ActiveVacancySetterConfig {
  vacancyId: number;
  customerId?: number;
}

export const NavigationContext = createContext<NavigationContextType>(
  undefined!
);

export const NavigationProvider: React.FC<React.ReactNode> = ({ children }) => {
  const [activeVacancy, _setActiveVacancy] = useState(
    getLocalAppSession()?.activeVacancy
  );

  const [activeCustomer, _setActiveCustomer] = useState(
    getLocalAppSession()?.activeCustomer
  );

  const [clearResourceSelection, setClearResourceSelection] = useState<
    () => void
  >(() => {});

  const navigate = useNavigate();

  const {
    refetch: refetchCurrentVacancy,
    data: vacancyData,
    loading: vacancyLoading,
  } = useQuery<{ vacancy: Vacancy }, { id: number; include: string }>(
    GET_VACANCY,
    {
      skip: !activeCustomer || !activeVacancy,
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      variables: {
        id: activeVacancy as number,
        include: 'brand.facebookPage,targeting',
      },
      onCompleted: (data) => {
        setActiveVacancy({
          vacancyId: data.vacancy.key,
          customerId: data.vacancy.customer_id,
        });
      },
    }
  );

  const { data: brandData, refetch: refetchBrands } = useQuery<
    { brands: Brand[] },
    {}
  >(GET_BRANDS, {
    skip: !activeCustomer,
    fetchPolicy: 'network-only',
  });

  const brands = useMemo(() => brandData?.brands ?? [], [brandData]);

  const currentVacancy = useMemo(() => vacancyData?.vacancy, [vacancyData]);

  const activeBrand = useMemo(() => {
    if (!currentVacancy) return undefined;
    return currentVacancy?.brand_id;
  }, [currentVacancy]);

  const { data: sessionData } = useQuery<SessionFlagsResponse>(
    GET_SESSION_FLAGS,
    {
      skip: !activeCustomer,
    }
  );

  const { data: customerData, refetch: refetchCustomer } = useQuery<
    { customer: Customer },
    { customerId: number }
  >(GET_CUSTOMER, {
    skip: !activeCustomer,
    variables: { customerId: activeCustomer as number },
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      if (!activeVacancy && data.customer.last_vacancy_id)
        setActiveVacancy({
          vacancyId: data.customer.last_vacancy_id,
          customerId: data.customer.id,
        });
    },
  });

  /**
   * SYM-6059. Same shape as the ad-template catalogue and the same trap: the answer is per
   * customer, but nothing in the query says so -- no variables, the customer travels in a header --
   * so Apollo does not ask again when the customer changes underneath. `network-only` decides what
   * happens when it runs; it does not make it run.
   *
   * This one carries which ad templates the customer has and which set their plan gets, so a stale
   * answer here is the picker describing somebody else.
   */
  const {
    data: subscriptionConfigurationData,
    refetch: refetchSubscriptionConfiguration,
  } = useQuery<{
    subscriptionConfiguration: SubscriptionConfiguration;
  }>(GET_SUBSCRIPTION_CONFIGURATION, {
    skip: !activeCustomer,
    fetchPolicy: 'network-only',
  });

  useRefetchOnCustomerChange(activeCustomer, refetchSubscriptionConfiguration);

  const isAdmin = useMemo(
    () =>
      import.meta.env.VITE_ADMIN_ENABLED === 'true' &&
      !!sessionData?.flags?.user?.is_sympl,
    [sessionData]
  );

  const jobPosition = useMemo(
    () => sessionData?.flags?.user?.job_position ?? '',
    [sessionData]
  );

  // TODO: actively use
  const customer = useMemo(
    () => customerData?.customer ?? null,
    [customerData]
  );

  const subscription = useMemo(
    () => customer?.subscription ?? null,
    [customer]
  );

  const subscriptionConfiguration = useMemo(
    () => subscriptionConfigurationData?.subscriptionConfiguration ?? null,
    [subscriptionConfigurationData]
  );

  const hasSubscriptionCapability = useCallback(
    (capability: SubscriptionCapabilityKey) =>
      subscriptionConfiguration?.capabilities?.[capability] === true,
    [subscriptionConfiguration]
  );

  /**
   * SYM-6051. WHETHER this customer has the new ad templates, asked here instead of asking the
   * feature-flag tool from the browser.
   *
   * The server resolves the flag already, for the catalogue, the preview and the write path. When
   * the browser asked it separately the two could answer differently -- and the visible form of
   * that was the picker offering a design the server then refused to draw.
   */
  const hasAdTemplates = useMemo(
    () => hasSubscriptionCapability(SUBSCRIPTION_CAPABILITIES.AD_TEMPLATES_V2),
    [hasSubscriptionCapability]
  );

  /**
   * SYM-6051. WHICH designs the plan gets.
   *
   * ⚠️ ANYTHING UNRECOGNISED READS AS FULL, matching the server exactly. A missing answer means an
   * older server or a request still in flight, and neither is a reason to tell a customer they are
   * on the small set. The failure direction has to be showing more than intended, never quietly
   * showing a paying customer less.
   *
   * Nothing is HIDDEN on the strength of this today -- the catalogue the server sends already
   * contains only the designs this customer may use, so the picker cannot offer a wrong one. It is
   * exposed so the locked-with-upgrade-prompt view can be built without touching this plumbing.
   */
  const adTemplateLevel = useMemo<AdTemplateLevel>(
    () =>
      subscriptionConfiguration?.ad_template_level ===
      AD_TEMPLATE_LEVELS.STARTER
        ? AD_TEMPLATE_LEVELS.STARTER
        : AD_TEMPLATE_LEVELS.FULL,
    [subscriptionConfiguration]
  );

  const hasVacancies = useMemo(
    () => customer?.has_vacancies ?? false,
    [customer]
  );

  const hasPublishedVacancy = useMemo(
    () => customer?.has_published_vacancy ?? false,
    [customer]
  );

  const customerColors = useMemo(() => customer?.colors ?? [], [customer]);

  useEffect(() => {
    if (!customer) return;

    const payload = {
      id: customer.id,
      name: customer.name,
      subscription: customer.subscription,
      vat: customer.vat,
      tier: customer?.subscription?.plan.tier,
      subscription_id: customer?.subscription?.id,
    };

    posthog.group('Customer', customer.id.toString(), payload);

    posthog.reloadFeatureFlags();

    setLocalAppSession({ customerName: customer.name });

    updateIntercom(getLocalAppSession()?.email ?? '', {
      customer: {
        ...payload,
      },
    });
  }, [customer]);

  const hasReachedVacsLimit = useMemo(
    () =>
      subscription === null
        ? false
        : subscription.current_live_vacs === subscription.max_live_vacs,
    [subscription]
  );

  useEffect(() => {
    // (RE)FETCH DATA
    if (activeCustomer) {
      refetchCustomer();
      refetchBrands();
    }
  }, [activeCustomer]);

  const setActiveCustomer = useCallback(
    async (customerId?: number) => {
      // Update the user session
      if (customerId !== activeCustomer) {
        setLocalAppSession({
          activeCustomer: customerId,
          activeVacancy: undefined,
        });
      }
    },
    [activeCustomer]
  );

  const unsetActiveVacancy = () => {
    setLocalAppSession({
      activeVacancy: undefined,
    });
  };

  const setActiveVacancy = useCallback(
    (config: ActiveVacancySetterConfig) => {
      const { vacancyId, customerId } = config;
      // Update the user session
      if (vacancyId !== activeVacancy) {
        setLocalAppSession(
          customerId
            ? {
                activeVacancy: vacancyId,
                activeCustomer: customerId,
              }
            : {
                activeVacancy: vacancyId,
              }
        );
      }
    },
    [activeVacancy]
  );

  useEffect(() => {
    const checkStorage = () => {
      const { activeCustomer: newCustomer, activeVacancy: newVacancy } =
        getLocalAppSession();

      if (!newCustomer && !newVacancy) return;

      if (newCustomer !== activeCustomer) {
        if (location.pathname.startsWith(`${Routes.PROCEDURES}/`))
          navigate(Routes.CANDIDATES);

        _setActiveCustomer(newCustomer);
      }

      if (newVacancy !== activeVacancy) _setActiveVacancy(newVacancy);
    };

    window.addEventListener('storage', checkStorage);

    return () => {
      window.removeEventListener('storage', checkStorage);
    };
  }, [activeCustomer, activeVacancy, navigate, setActiveVacancy]);

  const resetContext = () => {
    unsetActiveVacancy();
  };

  const [prevLocation, setPrevLocation] = useState<string>('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);

  return (
    <NavigationContext.Provider
      value={{
        activeVacancy,
        activeBrand,
        currentVacancy,
        setActiveVacancy,
        unsetActiveVacancy,
        activeCustomer,
        setActiveCustomer,
        isAdmin,
        jobPosition,
        refetchCurrentVacancy,
        hasVacancies,
        hasPublishedVacancy,
        hasUnsavedChanges,
        setHasUnsavedChanges,
        resetContext,
        prevLocation,
        setPrevLocation,
        clearResourceSelection,
        setClearResourceSelection,
        customerColors,
        customer,
        subscription,
        subscriptionConfiguration,
        hasSubscriptionCapability,
        hasAdTemplates,
        adTemplateLevel,
        hasReachedVacsLimit,
        refetchCustomer,
        vacIsPublished: currentVacancy?.published ?? false,
        vacSpendEnabled:
          currentVacancy?.spend_status !== VacancySpendStatus.NONE,
        hasSymplVacancyPage: !currentVacancy?.has_custom_url,
        brands,
        refetchBrands,
        vacancyLoading,
      }}
    >
      {children}
    </NavigationContext.Provider>
  );
};
